/**
 * The editable grid's state — pure (client, 2026-09-18: *"edit existing data"*).
 *
 * `RowsGrid.tsx` is the spreadsheet; this file is what it means. Every edit, insert and
 * delete is a move against one immutable `GridState`, and every move is pushed onto a log
 * so Undo is a pop rather than a re-derivation. Nothing here touches React, the DOM or the
 * network, so a 50 000-row edit session can be asserted in `grid.test.ts` without mounting
 * anything.
 *
 * **Deletes are marks, not removals.** A struck row stays in `rows` with `deleted: true`:
 * the reader can see what they are about to lose, Undo can bring it back, and the save
 * expresses the delete by *omitting* the row from the payload — which is the shape the
 * server's "the rows you send are the new version" contract wants.
 *
 * **The original values travel with the state.** Dirtiness is "differs from what was read",
 * not "was typed in": a reader who edits a cell and types the old value back has changed
 * nothing, and a Save button that still glows at them is a Save button that lies.
 */
import type { DatasetRowsAllColumn, DatasetRowsAllRow } from '../../lib/api';

export type CellKind = DatasetRowsAllColumn['kind'];
export type CellValue = string | number | boolean | null;

/**
 * One row of the grid.
 *
 * Two ids, deliberately: `id` is the server's `_id` **stringified**, which is what React
 * keys, test ids and the focus machinery need, while `srcId` is the value exactly as the
 * server sent it. The save sends `srcId` — a file-backed dataset keys its rows by their
 * integer file position, and posting `"0"` where it stored `0` makes the server refuse every
 * kept row as unknown. The client sends back what it received.
 */
export interface GridRow {
  id: string;
  srcId: string | number | null;
  values: Record<string, CellValue>;
  /** Added in this session — the save posts it without an `_id`. */
  added: boolean;
  /** Struck through — the save omits it. */
  deleted: boolean;
}

/** One undoable move. `before` is what the move overwrote. */
export type GridOp =
  | { type: 'edit'; id: string; key: string; before: CellValue }
  | { type: 'add'; id: string }
  | { type: 'delete'; id: string; before: boolean };

export interface GridState {
  rows: GridRow[];
  /** What the server sent, by row id — the yardstick for "dirty". */
  original: Record<string, Record<string, CellValue>>;
  /** Oldest first; Undo pops the end. */
  log: GridOp[];
  /** Counter behind the local ids of inserted rows, so two inserts never collide. */
  seq: number;
}

/** The prefix a row id carries while it exists only on this side. */
export const NEW_ROW_PREFIX = 'new-';

export const isNewRow = (id: string): boolean => id.startsWith(NEW_ROW_PREFIX);

/** The read, as a grid. Columns decide which keys a row carries — a stray key is dropped. */
export function initGrid(
  rows: readonly DatasetRowsAllRow[],
  columns: readonly DatasetRowsAllColumn[],
): GridState {
  const state: GridState = { rows: [], original: {}, log: [], seq: 0 };
  for (const raw of rows) {
    const id = String(raw._id);
    const values: Record<string, CellValue> = {};
    for (const col of columns) {
      const value = raw[col.key];
      values[col.key] = value === undefined ? null : (value as CellValue);
    }
    state.rows.push({ id, srcId: raw._id, values, added: false, deleted: false });
    state.original[id] = { ...values };
  }
  return state;
}

/**
 * A typed cell out of what was typed into it.
 *
 * An empty box is `null` in every kind — "no value" is a legitimate answer and the server
 * stores it — and a number is refused rather than coerced to `NaN`, because a silently
 * blanked cell is the one failure mode a grid must not have. Dates are ISO `YYYY-MM-DD`,
 * which is what `<input type="date">` produces and what the server parses.
 */
export function coerceCell(
  raw: string,
  kind: CellKind,
): { ok: true; value: CellValue } | { ok: false; error: 'number' | 'date' } {
  const text = raw.trim();
  if (text === '') return { ok: true, value: null };
  if (kind === 'number') {
    /*
      One comma, two meanings. `1,200` is a thousands separator on an anglophone keyboard and
      `1,2` is a decimal comma on a francophone one, and both reach this box from the same
      portal. The rule is the only one that separates them without asking: a comma followed by
      **exactly three digits** that end the number is a group separator and is dropped;
      anything else is a decimal point. `1,200.5` is therefore 1200.5 and `1,2` is 1.2.
    */
    // A lone leading zero (`0,123`) is a French decimal, never a group.
    const grouped = /^0,\d{3}$/.test(text) ? text : text.replace(/,(\d{3})(?=\D|$)/g, '$1');
    const n = Number(grouped.includes(',') ? grouped.replace(',', '.') : grouped);
    return Number.isFinite(n) ? { ok: true, value: n } : { ok: false, error: 'number' };
  }
  if (kind === 'date') {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(text) || Number.isNaN(Date.parse(text))) {
      return { ok: false, error: 'date' };
    }
    return { ok: true, value: text };
  }
  // Trimmed, because the server trims: a cell that reads `Bobo ` here and `Bobo` after the
  // save would come back dirty on the next open.
  return { ok: true, value: text };
}

/** The value in a cell, or `null` when the row has no such key. */
export const cellOf = (row: GridRow, key: string): CellValue => row.values[key] ?? null;

/**
 * Write one cell.
 *
 * A write of the value already there is *not* a move: it leaves the state identical, so it
 * costs no Undo step and cannot make a clean grid dirty. A write to a row that is not there
 * is likewise a no-op rather than a throw — the grid filters and windows, and a stale id
 * from a keyboard race must not take the panel down.
 */
export function applyEdit(state: GridState, id: string, key: string, value: CellValue): GridState {
  const index = state.rows.findIndex((r) => r.id === id);
  if (index === -1) return state;
  const row = state.rows[index]!;
  const before = cellOf(row, key);
  if (before === value) return state;
  const rows = [...state.rows];
  rows[index] = { ...row, values: { ...row.values, [key]: value } };
  return { ...state, rows, log: [...state.log, { type: 'edit', id, key, before }] };
}

/** Append an empty row. Tables only — the caller refuses it for vectors (`no_geometry`). */
export function addRow(state: GridState, columns: readonly DatasetRowsAllColumn[]): GridState {
  const id = `${NEW_ROW_PREFIX}${state.seq + 1}`;
  const values: Record<string, CellValue> = {};
  for (const col of columns) values[col.key] = null;
  return {
    ...state,
    seq: state.seq + 1,
    rows: [...state.rows, { id, srcId: null, values, added: true, deleted: false }],
    log: [...state.log, { type: 'add', id }],
  };
}

/** Strike a row through, or un-strike it — the same control both ways. */
export function deleteRow(state: GridState, id: string, deleted = true): GridState {
  const index = state.rows.findIndex((r) => r.id === id);
  if (index === -1) return state;
  const row = state.rows[index]!;
  if (row.deleted === deleted) return state;
  const rows = [...state.rows];
  rows[index] = { ...row, deleted };
  return { ...state, rows, log: [...state.log, { type: 'delete', id, before: row.deleted }] };
}

/**
 * Undo the last move.
 *
 * An added row is undone by removing it outright — it never existed server-side, so there is
 * nothing to restore it to — while an edit and a delete are restored to their `before`.
 */
export function undo(state: GridState): GridState {
  const last = state.log[state.log.length - 1];
  if (!last) return state;
  const log = state.log.slice(0, -1);
  if (last.type === 'add') {
    return { ...state, log, rows: state.rows.filter((r) => r.id !== last.id) };
  }
  const index = state.rows.findIndex((r) => r.id === last.id);
  if (index === -1) return { ...state, log };
  const row = state.rows[index]!;
  const rows = [...state.rows];
  rows[index] =
    last.type === 'edit'
      ? { ...row, values: { ...row.values, [last.key]: last.before } }
      : { ...row, deleted: last.before };
  return { ...state, rows, log };
}

/** Has this row changed since it was read? */
export function isDirtyRow(state: GridState, row: GridRow): boolean {
  if (row.added || row.deleted) return true;
  const before = state.original[row.id];
  if (!before) return true;
  return Object.keys(row.values).some((key) => (row.values[key] ?? null) !== (before[key] ?? null));
}

/** How many rows the save would change — what the dirty counter prints. */
export const dirtyCount = (state: GridState): number =>
  state.rows.filter((row) => isDirtyRow(state, row)).length;

/** Case-insensitive substring over every cell of the row — the grid's search box. */
export function matchesQuery(row: GridRow, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (needle === '') return true;
  return Object.values(row.values).some(
    (value) => value !== null && String(value).toLowerCase().includes(needle),
  );
}

export interface VersionPayload {
  columns: string[];
  rows: Array<Record<string, CellValue>>;
}

/**
 * The body of `POST /datasets/{id}/versions` — the new version, whole.
 *
 * Struck rows are simply not in it (that *is* the delete), kept rows carry their `_id` so
 * the server can match them, and an inserted row carries none. The column list travels too:
 * the server writes the rows under those keys and in that order, and sending it makes a
 * column the grid never showed impossible to lose.
 */
export function toPayload(
  state: GridState,
  columns: readonly DatasetRowsAllColumn[],
): VersionPayload {
  const keys = columns.map((c) => c.key);
  const rows = state.rows
    .filter((row) => !row.deleted)
    .map((row) => {
      const out: Record<string, CellValue> = {};
      // `srcId`, not `id`: see `GridRow`. An inserted row carries no `_id` at all.
      if (!row.added && row.srcId !== null) out._id = row.srcId;
      for (const key of keys) out[key] = row.values[key] ?? null;
      return out;
    });
  return { columns: keys, rows };
}
